import {
  Button,
  Card,
  TextInputField,
  Banner,
  GreyWhite,
  Primary14SemiBold,
  GreyGrey60,
  IconKey,
  IconEyeOpen,
  IconEyeSlash,
  Tooltip,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import { required, Validator } from '@sequencehq/utils/validation'
import Spinner from 'components/Loading/Spinner'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useMutation } from '@sequencehq/api/utils'
import { useEffect, useState, useCallback, useMemo } from 'react'

const numeralApiKeyValidation: Validator = (value: unknown) => {
  if (!value || typeof value !== 'string') {
    return 'API key is required'
  }
  if (!value.startsWith('sk_test_') && !value.startsWith('sk_prod_')) {
    return 'API key must start with sk_test_ or sk_prod_'
  }
  return undefined
}

const NUMERAL_DASHBOARD_URL = 'https://dashboard.numeralhq.com/developers'

type FormValues = {
  apiKey: string | undefined
}

interface Props {
  existingCredentials: FormValues
  onConnectionSuccess: (credentials: { apiKey: string }) => void
  onCredentialsChange: (credentials: FormValues) => void
}

const NumeralAuthenticationCard = ({
  existingCredentials,
  onConnectionSuccess,
  onCredentialsChange
}: Props) => {
  const isFirstTimeSetup = useMemo(
    () => !existingCredentials.apiKey,
    [existingCredentials.apiKey]
  )

  const [showApiKey, setShowApiKey] = useState(false)
  const [credentialValidationResult, setCredentialValidationResult] = useState<{
    status: 'success' | 'error' | null
    message?: string
  }>({ status: null })

  const { fields, queries } = useForm({
    value: existingCredentials,
    fieldConfiguration: [
      {
        property: 'apiKey',
        validation: [required, numeralApiKeyValidation]
      }
    ],
    onChange: onCredentialsChange
  })

  const {
    isError,
    isSuccess,
    isPending: isTestingCredentials,
    reset: resetTestCredentials,
    mutate: testCredentials
  } = useMutation(dashboardv99990101Client.postTestNumeralCredentials, {
    onSuccess: result => {
      if (result?.isValid && fields.apiKey.value) {
        setCredentialValidationResult({
          status: 'success',
          message:
            'Credentials verified! Your Numeral API key has been validated.'
        })
        onConnectionSuccess({
          apiKey: fields.apiKey.value
        })
      } else {
        setCredentialValidationResult({
          status: 'error',
          message:
            result?.message ||
            'Invalid credentials. Please check your API key and try again.'
        })
      }
    },
    onError: () => {
      setCredentialValidationResult({
        status: 'error',
        message:
          'Connection failed. Please check your network connection and try again.'
      })
    }
  })

  useEffect(() => {
    if (!isFirstTimeSetup) {
      resetTestCredentials()
    }
  }, [isFirstTimeSetup, resetTestCredentials])

  const handleApiKeyChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (isSuccess || isError || credentialValidationResult.status) {
        resetTestCredentials()
        setCredentialValidationResult({ status: null })
      }
      fields.apiKey.onChange(event?.target?.value)
    },
    [
      isSuccess,
      isError,
      credentialValidationResult.status,
      resetTestCredentials,
      fields.apiKey
    ]
  )

  const handleTestCredentials = useCallback(() => {
    if (fields.apiKey.value) {
      void testCredentials({
        apiKey: fields.apiKey.value
      })
    }
  }, [fields.apiKey.value, testCredentials])

  const toggleShowApiKey = useCallback(() => {
    setShowApiKey(prev => !prev)
  }, [])

  return (
    <Card>
      <Flex flexDirection="column" gap={6} data-testid="numeralAuth.card">
        <Box {...Primary14SemiBold}>Numeral authentication</Box>

        <Box>
          <Flex gap={3} alignItems="flex-end">
            <Box flex={1} minWidth={showApiKey ? '550px' : '450px'}>
              <TextInputField
                label="API Key"
                data-testid="numeralAuth.apiKey"
                value={fields.apiKey.value}
                onChange={handleApiKeyChange}
                validationErrors={fields.apiKey.validationErrors}
                placeholder="sk_test_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                type={showApiKey ? 'text' : 'password'}
                prefix={<IconKey color={GreyGrey60} />}
                fullWidth
                suffix={
                  <Tooltip
                    label={showApiKey ? 'Hide API key' : 'Show API key'}
                    placement="top"
                    shouldWrapChildren={false}
                  >
                    <Button
                      aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                      icon={showApiKey ? <IconEyeSlash /> : <IconEyeOpen />}
                      variant="unstyled"
                      size="16px"
                      onClick={toggleShowApiKey}
                    />
                  </Tooltip>
                }
                suffixStyles={{ pointerEvents: 'auto' }}
              />
            </Box>

            <Box minWidth="140px">
              <Button
                data-testid="numeralAuth.testCredentials"
                onClick={handleTestCredentials}
                disabled={isTestingCredentials || !queries.isValid}
                leadingIcon={
                  isTestingCredentials ? (
                    <Spinner color={GreyWhite} height="16px" width="16px" />
                  ) : undefined
                }
                variant="secondary"
              >
                {isTestingCredentials ? 'Checking...' : 'Test credentials'}
              </Button>
            </Box>
          </Flex>

          <Box mt={2} fontSize="13px" color={GreyGrey60}>
            Enter your Numeral API key (starting with <code>sk_test_</code> or{' '}
            <code>sk_prod_</code>) to connect your account. You can find this in
            your{' '}
            <Box
              as="a"
              href={NUMERAL_DASHBOARD_URL}
              target="_blank"
              rel="noopener noreferrer"
              color="blue.500"
              textDecoration="underline"
            >
              Numeral dashboard
            </Box>
            .
          </Box>
        </Box>

        {credentialValidationResult.status === 'success' && (
          <Banner data-testid="numeralAuth.banner.success" variant="success">
            {credentialValidationResult.message}
          </Banner>
        )}

        {credentialValidationResult.status === 'error' && (
          <Banner data-testid="numeralAuth.banner.error" variant="error">
            {credentialValidationResult.message}
          </Banner>
        )}
      </Flex>
    </Card>
  )
}

export default NumeralAuthenticationCard
